"use client";

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerNestedRoot,
  DrawerTitle,
} from "@workspace/ui/components/drawer";
import { VisuallyHidden } from "@workspace/ui/components/visually-hidden";
import { type Ref, useImperativeHandle, useRef, useState } from "react";
import { ListingList } from "./listing-list";
import { MarketplaceFilterControlsDrawer } from "./marketplace-filter-controls-drawer";
import { MarketplaceJobDetailsCard } from "./marketplace-job-details-card";
import type { MarketplaceJob } from "./use-cluster";

const SNAP_POINTS = [0.25, 0.6, 0.95] as const;
const DRAWER_MAX_HEIGHT =
  "h-[100dvh] data-[vaul-drawer-direction=bottom]:max-h-[100dvh]";
const DRAWER_CONTENT_CLASS =
  `flex ${DRAWER_MAX_HEIGHT} min-h-0 flex-col overflow-hidden` as const;

export type ListingsBottomSheetHandle = {
  snapTo: (point: (typeof SNAP_POINTS)[number]) => void;
};

type ListingsBottomSheetProps = {
  jobs: MarketplaceJob[];
  loading: boolean;
  userLatitude?: number;
  userLongitude?: number;
  /** Phase 5 wiring — threaded through to the list. */
  selectedId?: string | null;
  hoveredId?: string | null;
  onSelect?: (job: MarketplaceJob) => void;
  onHover?: (id: string | null) => void;
  /** The selected job to render in the NestedRoot detail drawer (mobile only). */
  selectedJob?: MarketplaceJob | null;
  onSelectedJobClose?: () => void;
};

export function ListingsBottomSheet({
  jobs,
  loading,
  userLatitude,
  userLongitude,
  selectedId,
  hoveredId,
  onSelect,
  onHover,
  selectedJob,
  onSelectedJobClose,
  ref,
}: ListingsBottomSheetProps & {
  ref?: Ref<ListingsBottomSheetHandle>;
}) {
  const [snap, setSnap] = useState<number | string | null>(SNAP_POINTS[0]);
  const lastSnapRef = useRef<number | string | null>(SNAP_POINTS[0]);
  const detailOpen = selectedJob !== null;

  useImperativeHandle(
    ref,
    () => ({
      snapTo: (point) => {
        if (detailOpen) {
          return;
        }
        lastSnapRef.current = point;
        setSnap(point);
      },
    }),
    [detailOpen]
  );

  const handleSnapChange = (newSnap: number | string | null) => {
    if (!detailOpen) {
      lastSnapRef.current = snap;
    }
    setSnap(newSnap);
  };

  return (
    <Drawer
      activeSnapPoint={snap}
      defaultOpen
      dismissible={false}
      fadeFromIndex={2}
      modal={false}
      setActiveSnapPoint={handleSnapChange}
      snapPoints={[...SNAP_POINTS]}
    >
      <DrawerContent className={DRAWER_CONTENT_CLASS}>
        <div className="flex shrink-0 items-center gap-3 border-border border-b bg-background px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground text-sm leading-5">
              {jobs.length.toLocaleString()}{" "}
              {jobs.length === 1 ? "job" : "jobs"}
            </p>
            <p className="truncate text-muted-foreground text-xs leading-4">
              Visible on the map
            </p>
          </div>
          <div className="ml-auto shrink-0">
            <MarketplaceFilterControlsDrawer
              userLatitude={userLatitude}
              userLongitude={userLongitude}
            />
          </div>
        </div>
        <ListingList
          hoveredId={hoveredId}
          jobs={jobs}
          loading={loading}
          onHover={onHover}
          onSelect={onSelect}
          selectedId={selectedId}
          userLatitude={userLatitude}
          userLongitude={userLongitude}
        />
      </DrawerContent>

      {/*
        Job-detail drawer mounts as a sibling of DrawerContent, inside the
        listings <Drawer> so it inherits vaul's context (DrawerNestedRoot).
        The parent scales back 16px while this is open — coordinated drag,
        no gesture conflict, parent owns body-scroll lock. On close we
        restore the parent's pre-detail snap so the sheet doesn't get
        stuck at 92% after a quick peek.
      */}
      <DrawerNestedRoot
        onOpenChange={(open) => {
          if (!open) {
            setSnap(lastSnapRef.current);
            onSelectedJobClose?.();
          }
        }}
        open={detailOpen}
      >
        <DrawerContent className={DRAWER_CONTENT_CLASS}>
          {selectedJob && (
            <VisuallyHidden>
              <DrawerHeader>
                <DrawerTitle>{selectedJob.title}</DrawerTitle>
                <DrawerDescription>
                  Job details for {selectedJob.title} at{" "}
                  {selectedJob.postedBy ?? "unknown"}
                </DrawerDescription>
              </DrawerHeader>
            </VisuallyHidden>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto px-4">
            {selectedJob && (
              <MarketplaceJobDetailsCard
                cardClassName="border-none bg-transparent shadow-none"
                footerClassName="px-6 py-3"
                job={selectedJob}
                showContentBorder={false}
              />
            )}
          </div>
        </DrawerContent>
      </DrawerNestedRoot>
    </Drawer>
  );
}
